import {
  AutocompleteOption,
  AutocompleteRenderOptionState,
  AutocompleteSlots,
  CircularProgress,
} from '@mui/joy';
import { AutocompleteInputChangeReason, SxProps } from '@mui/material';
import { debounce, isString } from 'lodash';
import {
  CSSProperties,
  forwardRef,
  KeyboardEvent,
  RefObject,
  SyntheticEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Control, FieldError, FieldValues } from 'react-hook-form';

import { CLICK_MOUSE_EVENT, ENTER_KEY, KEYDOWN_EVENT } from 'core/constants';
import { useInfiniteQuery } from 'core/hooks';
import { IAutocompleteOption, IInfiniteQueryOptions, IRequestPayload } from 'core/types';
import { deepEqual } from 'core/utils';
import { Size } from 'theme/enums';

import AutocompleteView from './AutocompleteView';
import { LOADING_LABEL } from './constants';
import { LoaderWrapper } from './style';
import { defaultRenderNewOption, defaultRenderOptions } from './utils';

export const DEFAULT_MIN_CHARS_TO_SEARCH = 3;

const DEFAULT_PAGE_SIZE = 30;
const DEFAULT_DEBOUNCE_TIMEOUT = 500;

export interface IAutocompleteProps {
  name: string;
  optionKey?: string;
  placeholder?: string;
  label?: string;
  extraLabel?: JSX.Element;
  tooltip?: string;
  required?: boolean;
  control?: Control<FieldValues, object>;
  error?: FieldError;
  pageSize?: number;
  value?: IAutocompleteOption;
  endDecorator?: JSX.Element;
  onEndDecoratorClick?: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  disableClearable?: boolean;
  renderNewOption?: (option: IAutocompleteOption) => JSX.Element;
  renderOptions?: (
    option: IAutocompleteOption,
    getLabel: (option: IAutocompleteOption) => string,
    inputValue?: string
  ) => JSX.Element;
  defaultOptions?: IAutocompleteOption[];
  getOptions?: (requestPayload: object, signal?: AbortSignal) => unknown;
  requestPayload?: IRequestPayload;
  queryOptions?: IInfiniteQueryOptions<unknown>;
  getKey?: (option: IAutocompleteOption) => string;
  getLabel?: (option: IAutocompleteOption) => string;
  onChange?: (_: SyntheticEvent, option: IAutocompleteOption) => void;
  onKeyDown?: (event: SyntheticEvent, search: string) => void;
  onFocus?: (event: SyntheticEvent) => void;
  slots?: AutocompleteSlots;
  hasCreateOption?: boolean;
  hideNewOptionIfMatched?: boolean;
  inputStyle?: SxProps;
  listboxStyle?: SxProps;
  autocompleteStyle?: SxProps;
  endDecoratorStyle?: SxProps;
  wrapperStyle?: SxProps;
  showPopupIndicator?: boolean;
  onNewOption?: (option: IAutocompleteOption) => void;
  size?: Size;
  multiple?: boolean;
  enabledOnFocus?: boolean;
  minCharsToSearch?: number;
  limitTags?: number;
  applyDefaultQuery?: boolean;
  // Default query from value (label displayed when option is selected)
  // Used when applying default query is true
  getQueryFromValue?: (value: string) => string;
  ref?: RefObject<{
    refetch: () => void;
    setInputValue: (value: string) => void;
  }>;
  includeFilterQuery?: boolean;
  groupBy?: (option: IAutocompleteOption) => string;
  renderGroup?: (params: IAutocompleteOption) => JSX.Element;
  getOptionDisabled?: (option: IAutocompleteOption) => boolean;
  helperTexts?: string[] | JSX.Element[];
  helperTextStyle?: CSSProperties;
  maxLength?: number;
  // Controls MUI's visible input so it can be wiped to empty after each pick. Opt-in for the
  // multi-select-into-chip-list pickers; otherwise the input stays uncontrolled (MUI keeps the
  // selected option's label, the normal single-select behaviour).
  clearInputOnSelect?: boolean;
}

export const Autocomplete = forwardRef(function Autocomplete(
  {
    name,
    optionKey = 'id',
    placeholder = '',
    label,
    extraLabel,
    tooltip,
    required = false,
    control,
    error,
    pageSize = DEFAULT_PAGE_SIZE,
    value,
    endDecorator,
    onEndDecoratorClick,
    disabled = false,
    autoFocus = false,
    disableClearable = false,
    renderNewOption = defaultRenderNewOption,
    renderOptions = defaultRenderOptions,
    defaultOptions = [],
    getOptions,
    requestPayload = {},
    queryOptions = {},
    getKey = (option) => option?.[optionKey],
    getLabel = (option) => option.name,
    onChange,
    onKeyDown,
    onFocus,
    slots,
    hasCreateOption = false,
    hideNewOptionIfMatched = true,
    inputStyle = {},
    listboxStyle = {},
    autocompleteStyle = {},
    endDecoratorStyle = {},
    wrapperStyle = {},
    showPopupIndicator = true,
    onNewOption,
    size = Size.SM,
    multiple = false,
    enabledOnFocus = true,
    minCharsToSearch = 0,
    limitTags,
    applyDefaultQuery = false,
    getQueryFromValue = (value: string) => value,
    includeFilterQuery = false,
    groupBy,
    renderGroup,
    getOptionDisabled,
    helperTexts = [],
    helperTextStyle = {},
    maxLength,
    clearInputOnSelect = false,
  }: IAutocompleteProps,
  ref
) {
  const [inputValue, setInputValue] = useState<string>('');
  // Synchronous mirror of the visible text, used only when clearInputOnSelect controls MUI's
  // input. Kept apart from `inputValue` (which is debounced and drives the query) so typing
  // doesn't lag behind the 500ms debounce.
  const [displayInputValue, setDisplayInputValue] = useState<string>('');
  const [options, setOptions] = useState<IAutocompleteOption[]>(defaultOptions);
  const [isFocused, setIsFocused] = useState(!enabledOnFocus);

  const customRequestPayload = useMemo(() => {
    if (!includeFilterQuery) {
      return { ...requestPayload, pageSize, q: inputValue };
    }

    return {
      ...requestPayload,
      pageSize,
      filters: { ...((requestPayload?.filters as object) || {}), query: inputValue },
    };
  }, [includeFilterQuery, requestPayload, pageSize, inputValue]);

  const enableGetOptions = useMemo(() => {
    return !!getOptions && isFocused && inputValue?.length >= minCharsToSearch;
  }, [getOptions, isFocused, inputValue, minCharsToSearch]);

  const { records, fetchNextPage, isFetchingNextPage, isLoading, refetch } = useInfiniteQuery(
    getOptions as any,
    [name, customRequestPayload],
    customRequestPayload,
    {
      ...queryOptions,
      enabled: enableGetOptions,
    }
  );

  // Observer for infinite scroll
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (!getOptions && defaultOptions.length > 0 && !deepEqual(defaultOptions, options)) {
      setOptions(defaultOptions);
    }
  }, [defaultOptions, getOptions, options]);

  useEffect(() => {
    setOptions(records);
  }, [records]);

  const handleGetKey = (option: IAutocompleteOption) => {
    if (option?.isLoading) {
      return LOADING_LABEL;
    }

    if (getKey) {
      return getKey(option);
    }

    return option?.[optionKey] || option;
  };

  const handleGetLabel = (option: IAutocompleteOption) => {
    if (option?.isLoading) {
      return '';
    }

    // Add new option created dynamically
    if (hasCreateOption && option?.[optionKey] && option?.isNew) {
      return option?.[optionKey];
    }

    return getLabel(option) || option?.label || (isString(option) ? option : '');
  };

  const debouncedHandleInputChange = debounce(
    (_: SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
      if (reason === 'input' && LOADING_LABEL !== value) {
        setInputValue(value);
      }

      if (reason === 'reset' || reason === 'clear') {
        setInputValue(applyDefaultQuery ? getQueryFromValue(value) : '');
        refetch();
      }
    },
    DEFAULT_DEBOUNCE_TIMEOUT
  );

  const handleInputChange = (
    event: SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (clearInputOnSelect) {
      // Drop the option-label echo MUI writes on selection; otherwise mirror the typed text now
      // (the query refetch below stays debounced).
      setDisplayInputValue(reason === 'reset' || reason === 'clear' ? '' : value);
    }

    debouncedHandleInputChange(event, value, reason);
  };

  const handleOnChange = (event: SyntheticEvent, option: IAutocompleteOption) => {
    if (clearInputOnSelect && option) {
      setDisplayInputValue('');
    }

    if (hasCreateOption && option?.isNew) {
      const isMouseEvent = event.type === CLICK_MOUSE_EVENT;
      const isKeyboardEnter =
        event.type === KEYDOWN_EVENT && (event as KeyboardEvent).key === ENTER_KEY;

      if (onNewOption && (isMouseEvent || isKeyboardEnter)) {
        onNewOption(option);
      } else if (onChange && isMouseEvent) {
        onChange(event, option);
      } else if (onChange && options.length) {
        onChange(event, options[0]);
      }

      return;
    }

    if (onChange) {
      onChange(event, option);
    }
  };

  const handleOnFocus = (event: SyntheticEvent) => {
    enabledOnFocus && setIsFocused(true);

    if (enableGetOptions) {
      refetch();
    }

    onFocus?.(event);
  };

  const handleOnBlur = () => {
    enabledOnFocus && setIsFocused(false);
  };

  // Last element of the list
  const lastElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (isLoading || isFetchingNextPage) {
        return;
      }

      // Disconnect the observer if it's already connected
      if (observer.current) {
        observer.current.disconnect();
      }

      // Check if the observer is intersecting (visible on the screen)
      observer.current = new IntersectionObserver((entries) => {
        // If the observer element is visible (we only observe one), fetch more data
        if (entries[0].isIntersecting) {
          // Load more by increasing the page
          fetchNextPage();
        }
      });

      // If there is a node, observe it
      if (node) {
        observer.current.observe(node);
      }
    },
    [isLoading, isFetchingNextPage, fetchNextPage]
  );

  const handleRenderOptions = (
    props: AutocompleteRenderOptionState,
    option: IAutocompleteOption,
    inputValue: string
  ) => {
    const isLast = option === options?.[options.length - 1];
    const extraProps = isLast ? { ref: lastElementRef } : {};

    // Render loading spinner as the last item
    if (option?.isLoading) {
      return (
        <AutocompleteOption {...props} {...extraProps}>
          <LoaderWrapper>
            <CircularProgress size={Size.SM} />
          </LoaderWrapper>
        </AutocompleteOption>
      );
    }

    if (hasCreateOption && option?.[optionKey] && option?.isNew) {
      return (
        <AutocompleteOption {...props} {...extraProps}>
          {renderNewOption(option)}
        </AutocompleteOption>
      );
    }

    return (
      <AutocompleteOption {...props} {...extraProps}>
        {renderOptions(option, getLabel, inputValue)}
      </AutocompleteOption>
    );
  };

  const handleIsOptionEqualToValue = (option: IAutocompleteOption, value: IAutocompleteOption) => {
    if (!options?.length && !option?.isLoading && !option?.isNew) {
      return true;
    }

    return !option?.isLoading && option?.[optionKey] === value?.[optionKey];
  };

  const handleFilterOptions = (options: IAutocompleteOption[]) => {
    if (!hasCreateOption) {
      return options;
    }

    const isExisting = options.some((option) => {
      const optionValue = getLabel(option) || option?.[handleGetKey(option)];

      if (typeof inputValue === 'string' && typeof optionValue === 'string') {
        return inputValue.toLowerCase() === optionValue.toLowerCase();
      }

      return inputValue === optionValue;
    });

    if (inputValue !== '' && (!hideNewOptionIfMatched || !isExisting)) {
      options.unshift({
        [optionKey]: inputValue,
        title: inputValue,
        isNew: true,
      });
    }

    return options;
  };

  useImperativeHandle(ref, () => ({
    refetch: () => refetch(),
    setInputValue: (value: string) => setInputValue(value),
  }));

  return (
    <AutocompleteView
      name={name}
      placeholder={placeholder}
      label={label}
      extraLabel={extraLabel}
      tooltip={tooltip}
      required={required}
      control={control}
      error={error}
      value={value}
      inputValue={inputValue}
      controlledInputValue={clearInputOnSelect ? displayInputValue : undefined}
      options={options || []}
      loading={isLoading || isFetchingNextPage}
      getOptionKey={handleGetKey}
      getOptionLabel={handleGetLabel}
      isOptionEqualToValue={handleIsOptionEqualToValue}
      handleInputChange={handleInputChange}
      renderOptions={handleRenderOptions}
      endDecorator={endDecorator}
      onEndDecoratorClick={onEndDecoratorClick}
      handleOnChange={handleOnChange}
      handleOnKeyDown={onKeyDown}
      handleOnFocus={handleOnFocus}
      handleOnBlur={handleOnBlur}
      handleFilterOptions={handleFilterOptions}
      size={size}
      hasCreateOption={hasCreateOption}
      slots={slots}
      disabled={disabled}
      autoFocus={autoFocus}
      disableClearable={disableClearable}
      inputStyle={inputStyle}
      listboxStyle={listboxStyle}
      autocompleteStyle={autocompleteStyle}
      endDecoratorStyle={endDecoratorStyle}
      wrapperStyle={wrapperStyle}
      showPopupIndicator={showPopupIndicator}
      multiple={multiple}
      minCharsToSearch={minCharsToSearch}
      groupBy={groupBy}
      renderGroup={renderGroup}
      getOptionDisabled={getOptionDisabled}
      helperTexts={helperTexts}
      helperTextStyle={helperTextStyle}
      limitTags={limitTags}
      maxLength={maxLength}
    />
  );
});
